import { isCamaraActiva } from "../../utils/camaraStatus.js";
import React, { useState, useEffect } from 'react';
import { CamHardWare } from "../model/StockCam"; 
import { ToastContainer, toast } from 'react-toastify';
import { notifySlotQuotaError } from "../../utils/slotQuota.js";
import { updateStockCam, createStockCam } from '../api/stockCams.api'
import { getAllCustomers } from '../../customerManager/api/CustomerManager.api';
import { getCustomerCamaras, updateCamaraById, getCustomerCamarasWithoutStockCam } from '../../camaraManager/api/camaraManager.api';

interface SaveAndEditCamModalProps {
  isOpen: boolean;
  onClose: () => void;
  stockCam?: CamHardWare; 
}

const SaveAndEditCamModal: React.FC<SaveAndEditCamModalProps> = ({ isOpen, onClose, stockCam}) => {
  
  const brands = ['Reolink', 'Camhi Pro', 'Dahua', 'Hikvision'];
  const owners = ['Lisual', 'Tercero'];
  const isAssigned = Boolean(stockCam?.customerId && stockCam?.camIdentifier);


  const [cam, setCam] = useState<CamHardWare>({
    id: '',
    brand: '',
    serialNumber: '',
    model: '',
    type: '',
    createdAt: '',
    assignedDate: '',
    customerId: '',
    camIdentifier: '',
    slotCamId: '',
    status: false,
    pwd: '',
    owner: '',
  });

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [camsByCustomer, setCamsByCustomer] = useState<any[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingCams, setLoadingCams] = useState(false);



  // cargar clientes al abrir modal
  useEffect(() => {
  if (isOpen) {
    setLoadingCustomers(true);
    getAllCustomers()
      .then((res) => {
        const sorted = [...res].sort((a, b) => {
          const nameA = (a.name || a.customerId || "").toLowerCase();
          const nameB = (b.name || b.customerId || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setCustomers(sorted);
      })
      .catch((err) => console.error("Error al cargar clientes:", err))
      .finally(() => setLoadingCustomers(false));
  }
}, [isOpen]);



  // cargar cámaras cuando cambia el cliente
 useEffect(() => {
  if (cam.customerId) {
    setLoadingCams(true);
    getCustomerCamarasWithoutStockCam(cam.customerId)
      .then((res) => {
        // mapeo: id -> slotCamId, y se excluyen las cámaras con obra terminada
        const mapped = res
          .filter((c: any) => isCamaraActiva(c))
          .map((c: any) => ({
            ...c,
            slotCamId: c.id,
          }));


        const sorted = [...mapped].sort((a, b) =>
          (a.identifier || "").localeCompare(b.identifier || "")
        );

        setCamsByCustomer(sorted);
      })
      .catch((err) => console.error("Error al cargar cámaras del cliente:", err))
      .finally(() => setLoadingCams(false));
  } else {
    setCamsByCustomer([]);
  }
}, [cam.customerId]);


  // abre con la carga o sin nada para crear
  useEffect(() => {
    if (stockCam) {
      setCam(stockCam);
    } else {
      setCam({
        id: '',
        brand: '',
        serialNumber: '',
        model: '',
        type: '',
        createdAt: '',
        assignedDate: '',
        customerId: '',
        camIdentifier: '',
        slotCamId: '',
        status: true,
        pwd: '',
        owner: '',
      });
    }
  }, [stockCam]);


  //maneja los cambios
const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const inputValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    if (name === "camIdentifier") {
        const selectedCam = camsByCustomer.find(c => c.identifier === value);
        if (selectedCam) {
            setCam(prevCam => ({
                ...prevCam,
                camIdentifier: selectedCam.identifier,
                slotCamId: selectedCam.slotCamId
            }));
        }
    } else {
        setCam(prevCam => ({ ...prevCam, [name]: inputValue }));
    }
  };


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log("Saving CamHardWare and assigning slot:", cam);

      
      if (cam.id && cam.id.trim() !== "") {
        //UPDATE
        const now = new Date();
        const camWithAssignedDate = {
          ...cam,
          assignedDate: now.toISOString(),
        };
        await updateStockCam(cam.id, camWithAssignedDate);
      } else {
        //CREATE
        await createStockCam(cam);
      }
      
      //actualiza el camara slot (solo si hay un slot nuevo seleccionado)
      if (cam.slotCamId) {
        const filteredData = {
          "camaraUID":cam.serialNumber,
        }
        await updateCamaraById(cam.slotCamId, filteredData);
      }

      toast.success(`Cámara ${cam.id ? 'editada' : 'guardada'} exitosamente`);
      setLoading(false);
      onClose();
    } catch (error: any) {
      console.error("Error al guardar la cámara:", error);
      if (!notifySlotQuotaError(error, toast)) toast.error("Error al guardar la cámara.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-12 z-50 overflow-y-auto">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl my-8 mx-4">
        
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="text-xl font-bold">{stockCam ? 'Editar Cámara' : 'Añadir Nueva Cámara'}</h2>
          <button className="text-gray-500 hover:text-gray-700 text-2xl" onClick={onClose}>&times;</button>
        </div>
        
        <div>
          <form onSubmit={handleSubmit}>
            
            {/* Marca */}
            <div className="mb-4">
              <label htmlFor="brand" className="block text-gray-700 font-medium mb-1">Marca *</label>
              <select 
                id="brand" 
                name="brand" 
                value={cam.brand} 
                onChange={handleInputChange} 
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" 
                required
              >
                <option value="" disabled>Seleccione una marca</option>
                {brands.map((brand) => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            {/* Modelo */}
            <div className="mb-4">
              <label htmlFor="model" className="block text-gray-700 font-medium mb-1">Modelo *</label>
              <input type="text" id="model" name="model" value={cam.model} onChange={handleInputChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" required />
            </div>

            {/* Serie */}
            <div className="mb-4">
              <label htmlFor="serialNumber" className="block text-gray-700 font-medium mb-1">
                UID/Nº Serie *
              </label>
              <input 
                type="text" 
                id="serialNumber" 
                name="serialNumber" 
                value={cam.serialNumber} 
                onChange={handleInputChange} 
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 
                  ${isAssigned ? "bg-gray-100 cursor-not-allowed" : ""}`}
                required 
                readOnly={isAssigned}   
              />
            </div>

            {/* Tipo */}
            <div className="mb-4">
              <label htmlFor="type" className="block text-gray-700 font-medium mb-1">Tipo *</label>
              <input type="text" id="type" name="type" value={cam.type} onChange={handleInputChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" required />
            </div>

            {/* Contraseña */}
            <div className="mb-4">
              <label htmlFor="pwd" className="block text-gray-700 font-medium mb-1">Contraseña *</label>
              <input type="text" id="pwd" name="pwd" value={cam.pwd} onChange={handleInputChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" required />
            </div>

            {/* Propietario */}
            <div className="mb-4">
              <label htmlFor="owner" className="block text-gray-700 font-medium mb-1">Propietario *</label>
              <select
                id="owner"
                name="owner"
                value={cam.owner}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="" disabled>Seleccione un propietario</option>
                {owners.map((owner) => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
            </div>

             {/* Cliente */}
            <div className="mb-4">
              <label htmlFor="customerId" className="block text-gray-700 font-medium mb-1">Cliente *</label>
              {loadingCustomers ? (
                <p>Cargando clientes...</p>
              ) : (
                <select 
                  id="customerId" 
                  name="customerId" 
                  value={cam.customerId} 
                  onChange={handleInputChange} 
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 
                    ${isAssigned ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  required
                  disabled={isAssigned}
                >
                  <option value="" disabled>Seleccione un cliente</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.customerId}>
                      {c.name || c.customerId}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Cámara por cliente */}
            <div className="mb-4">
              <label htmlFor="camIdentifier" className="block text-gray-700 font-medium mb-1">Identificador de la Cámara *</label>
              {loadingCams ? (
                <p>Cargando cámaras...</p>
              ) : (
                <select 
                  id="camIdentifier" 
                  name="camIdentifier" 
                  value={cam.camIdentifier} 
                  onChange={handleInputChange} 
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 
                    ${isAssigned ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  required
                  disabled={!cam.customerId || isAssigned}
                >
                  <option value="" disabled>Seleccione una cámara</option>
                  {camsByCustomer.map((c) => (
                    <option key={c.identifier} value={c.identifier}>{c.identifier}</option>
                  ))}
                </select>
              )}
            </div>
            
            {/* Fechas */}
            <div className="mb-4">
              <label htmlFor="createdAt" className="block text-gray-700 font-medium mb-1">Fecha de Creación</label>
              <input type="text" id="createdAt" name="createdAt" value={cam.createdAt ? new Date(cam.createdAt).toLocaleDateString('es-ES') : ""} readOnly className="w-full p-2 border rounded-md bg-gray-100" />
            </div>

            <div className="mb-4">
              <label htmlFor="assignedDate" className="block text-gray-700 font-medium mb-1">Fecha de Asignación</label>
              <input type="text" id="assignedDate" name="assignedDate" value={cam.assignedDate ? new Date(cam.assignedDate).toLocaleDateString('es-ES'): ""} readOnly className="w-full p-2 border rounded-md bg-gray-100" />
            </div>

            {/* Activa */}
            <div className="mb-4 flex items-center">
              <input type="checkbox" id="status" name="status" checked={cam.status} onChange={handleInputChange} className="mr-2" />
              <label htmlFor="status" className="text-gray-700 font-medium">Activa</label>
            </div>
            
            <hr className="my-6 border-gray-200" />

            {/* Botones */}
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 bg-gray-500 text-white rounded-md" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded-md" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SaveAndEditCamModal;
